import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { putRequest } from "../../Requests/postRequests";
import { fetchRequests } from "../../Requests/getRequests";
import { show } from '../../slices/toastSlice';
import Logo from "../../assets/images/logo.png";

// Landing page for the staff password-reset email: checks the one-time link,
// then sets the new password with it.
function ResetPassword() {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token") || "";

    const [account, setAccount] = useState(null);
    const [linkError, setLinkError] = useState("");
    const [loading, setLoading] = useState(true);
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [process, setProcess] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const check = async () => {
            const res = await fetchRequests(`/auth/staff/reset_password`, "GET", {}, { token }, {});
            if (cancelled) return;
            if (!res || res?.err) setLinkError(res?.err || "This reset link is invalid or has expired. Request a new one.");
            else setAccount(res);
            setLoading(false);
        };
        check();
        return () => { cancelled = true; };
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (password.length < 8) {
            dispatch(show({ type: "error", state: true, message: "Password must be at least 8 characters" }));
            return;
        }
        if (password !== confirm) {
            dispatch(show({ type: "error", state: true, message: "Passwords do not match" }));
            return;
        }

        setProcess(true);
        const res = await putRequest(`/auth/staff/reset_password`, { token, password });
        setProcess(false);

        if (!res || res?.err) {
            dispatch(show({ type: "error", state: true, message: res?.err || "Something went wrong. Please try again." }));
            return;
        }

        dispatch(show({ type: "success", state: true, message: res.message }));
        navigate("/login");
    };

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-gray-950 flex items-center justify-center p-4">
            <div className="w-full max-w-sm">

                <div className="flex justify-center mb-8">
                    <img src={Logo} alt="CedisPay" className="h-10 w-auto object-contain" />
                </div>

                <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-8">
                    {loading ? (
                        <p className="text-sm text-gray-500 dark:text-gray-400 text-center">Checking your link…</p>
                    ) : linkError ? (
                        <>
                            <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">Reset link not valid</h1>
                            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{linkError}</p>
                            <Link to="/forgot-password" className="mt-6 inline-block text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline">
                                Request a new link
                            </Link>
                        </>
                    ) : (
                        <>
                            <div className="mb-7">
                                <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
                                    Set new password
                                </h1>
                                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                    Choose a new password for{" "}
                                    <span className="font-medium text-gray-700 dark:text-gray-300">{account?.email}</span>
                                </p>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-5">
                                <div>
                                    <label htmlFor="password" className="form-label">New password</label>
                                    <input
                                        type="password"
                                        id="password"
                                        name="password"
                                        autoComplete="new-password"
                                        required
                                        minLength={8}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="form-input"
                                        placeholder="At least 8 characters"
                                    />
                                </div>

                                <div>
                                    <label htmlFor="confirm" className="form-label">Confirm password</label>
                                    <input
                                        type="password"
                                        id="confirm"
                                        name="confirm"
                                        autoComplete="new-password"
                                        required
                                        value={confirm}
                                        onChange={(e) => setConfirm(e.target.value)}
                                        className="form-input"
                                        placeholder="••••••••"
                                    />
                                </div>

                                <button
                                    type="submit"
                                    disabled={process}
                                    className="w-full h-11 flex items-center justify-center rounded-xl bg-light-blue hover:bg-dark-blue text-white text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed mt-2"
                                >
                                    {process ? "Saving…" : "Reset password"}
                                </button>
                            </form>
                        </>
                    )}
                </div>

                <p className="mt-5 text-center">
                    <Link
                        to="/login"
                        className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                    >
                        Back to sign in
                    </Link>
                </p>
            </div>
        </div>
    );
}

export default ResetPassword;
